import {
  INSURANCE_PLAN_NAME_ECO_RP,
  INSURANCE_PLAN_NAME_ECO_RP_HPE,
  INSURANCE_PLAN_NAME_ECO_YP,
  INSURANCE_PLAN_NAME_RP,
  INSURANCE_PLAN_NAME_RP_HPE,
  INSURANCE_PLAN_NAME_SCO_RP,
  INSURANCE_PLAN_NAME_SCO_RP_HPE,
  INSURANCE_PLAN_NAME_SCO_YP,
  INSURANCE_PLAN_NAME_STAX_RP,
  INSURANCE_PLAN_NAME_STAX_RP_HPE,
  INSURANCE_PLAN_NAME_YP,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ArrowDownIcon } from "components/Icons/Icons";

import { PROGRAM_NAME_ARCCO, PROGRAM_NAME_PLC } from "../../ArcPlc/ArcPlcService";
import {
  STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE,
} from "../helpers";

const InsuranceCoverageOverlapChart = ({
  budgetCountyPractice,
  countyData,
  insurancePlans,
  stressTestDisplayField,
  arcCoCombinedResults,
  arcPlc,
  budgetTotalReportedAcreage,
  programElection,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Map parameters for the chart URL
  const getChartUrl = () => {
    if (!budgetCountyPractice || !countyData) return null;

    const price = countyData?.projected_price || 0;
    const farmYield = budgetCountyPractice?.approved_yield || 0;
    const countyYield = countyData?.expected_index_value || 0;
    const practice = budgetCountyPractice?.practice_name || "";
    const county = (budgetCountyPractice?.county_name || "") + ", " + (budgetCountyPractice?.state_name || "");
    const crop = budgetCountyPractice?.commodity_name || "";
    const year = budgetCountyPractice?.year || "";

    // Find coverage levels for different insurance plan families
    let rpCov = 0;
    let scoCov = 0;
    let ecoCov = 0;

    if (insurancePlans?.length) {
      // Find RP coverage level (individual plans) - Revenue Protection
      const rpPlan = insurancePlans.find(
        (plan) =>
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_RP ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_RP_HPE ||
          plan.insurance_plan_name === "Revenue Protection HPE" ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_YP
      );
      if (rpPlan) {
        rpCov = rpPlan.coverage_level_percent || 0;
      }

      // Find SCO coverage level - Supplemental Coverage Option
      const scoPlan = insurancePlans.find(
        (plan) =>
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_SCO_RP ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_SCO_RP_HPE ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_SCO_YP ||
          plan.insurance_plan_name === "SCO RP" ||
          plan.insurance_plan_name === "SCO RP-HPE" ||
          plan.insurance_plan_name === "SCO YP"
      );
      if (scoPlan) {
        scoCov = scoPlan.coverage_level_percent || 0;
      }

      // Find ECO coverage level - Enhanced Coverage Option
      const ecoPlan = insurancePlans.find(
        (plan) =>
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_ECO_RP ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_ECO_RP_HPE ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_ECO_YP ||
          plan.insurance_plan_name === "ECO RP" ||
          plan.insurance_plan_name === "ECO RP-HPE" ||
          plan.insurance_plan_name === "ECO YP"
      );
      if (ecoPlan) {
        ecoCov = ecoPlan.coverage_level_percent || 0;
      }
    }

    // Determine if ARC or PLC is elected
    const isArcElected = programElection?.program_election === PROGRAM_NAME_ARCCO;
    const isPlcElected = programElection?.program_election === PROGRAM_NAME_PLC;

    const params = new URLSearchParams({
      price: price.toString(),
      farm_yield: farmYield.toString(),
      county_yield: countyYield.toString(),
      rp_cov: rpCov.toString(),
      sco_cov: scoCov > 0 ? 0.86 : 0, //SCO is 86% if elected, otherwise 0
      eco_cov: ecoCov.toString(),
      type: "image",
      practice: practice,
      county: county,
      crop: crop,
      // Pass 0 for ARC params if PLC is elected
      arc_benchmark_yield: isPlcElected ? 0 : arcCoCombinedResults?.benchmark_yield || 0,
      arc_benchmark_price: isPlcElected ? 0 : arcCoCombinedResults?.benchmark_price || 0,
      theme: theme.palette.mode || "light",
      mobile: isMobile || false,
      orientation: isMobile ? "vertical" : "horizontal",
      year: year,
    });
    // PLC Parameters - Pass 0 if ARC is elected
    let plcReferencePrice = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.effective_reference_price || 0;
    let plcMyaPrice = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.effective_price || 0;
    let plcYield = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.plc_yield || 0;
    let plcBaseShare = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.share_percent || 0;
    let plcBaseAcres = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.base_acreage || 0;
    let plcPlantedAcres = isArcElected ? 0 : budgetTotalReportedAcreage || 0;
    let plcLoanRate = isArcElected ? 0 : arcPlc?.totals?.plcCalculation?.inputs?.national_loan_rate || 0;

    // STAX (Cotton-specific)

    let staxCovHi = 0;
    let staxCovLo = 0;
    let staxPriceElection = 0;

    if (insurancePlans?.length) {
      const staxPlan = insurancePlans.find(
        (plan) =>
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_STAX_RP ||
          plan.insurance_plan_name === INSURANCE_PLAN_NAME_STAX_RP_HPE
      );
      if (staxPlan) {
        staxCovHi = staxPlan.coverage_level_percent || 0;
        staxCovLo = staxPlan.underlying_coverage_level_percent || 0;
        staxPriceElection = staxPlan.price_election_percent || 0;
      }
    }
    params.append("stax_cov_hi", staxCovHi.toString());
    params.append("stax_cov_lo", staxCovLo.toString());
    params.append("stax_price_election", staxPriceElection.toString());
    params.append("plc_reference_price", plcReferencePrice.toString());
    params.append("plc_mya_price", plcMyaPrice.toString());
    params.append("plc_yield", plcYield.toString());
    params.append("plc_base_share", plcBaseShare.toString());
    params.append("plc_base_acres", plcBaseAcres.toString());
    params.append("plc_planted_acres", plcPlantedAcres.toString());
    params.append("plc_loan_rate", plcLoanRate.toString());

    const chartUrl = `${import.meta.env.VITE_COVERAGE_CHART_BASE_URL}plot?${params.toString()}`;

    return chartUrl;
  };

  const chartUrl = getChartUrl();

  // Only show if we have the required data and are showing insurance benefit fields
  const shouldShow =
    chartUrl &&
    [STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE].includes(
      stressTestDisplayField
    );

  if (!shouldShow) return null;

  return (
    <Box sx={{ maxWidth: "100%", mb: 2, pt: 2 }}>
      <Accordion
        disableGutters
        sx={{
          backgroundColor: "background.paper",
          "&.Mui-expanded": {
            backgroundColor: "grey.100",
          },
          "&:before": {
            display: "none",
          },
          border: "none",
          borderRadius: "0.875rem !important",
          boxShadow: "none",
          mb: 2,
        }}
      >
        <AccordionSummary
          sx={{
            padding: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              margin: "0 0 0 1rem",
            },
          }}
          expandIcon={
            <ArrowDownIcon
              sx={{
                width: 48,
                height: 48,
                color: "primary.main",
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                  color: "primary.dark",
                },
              }}
            />
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
            <Typography
              sx={{ fontSize: "1.25rem !important", fontWeight: "600 !important", mb: "0 !important" }}
              variant="h5"
            >
              Insurance Coverage Overlap Chart
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ "&>.MuiStack-root": { gap: 1 } }}>
          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Box
              component="img"
              src={chartUrl}
              alt="Insurance Coverage Overlap Chart"
              sx={{
                maxWidth: "100%",
                width: "100%",
                height: "auto",
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default InsuranceCoverageOverlapChart;
